import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  Box, List, ListItem, ListItemText, Modal, Typography,
  IconButton, Card, CardMedia, CardContent,
  Grid,
  Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useWebSocket } from "../WebSocketContext/WebSocketContext";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentMessages, setCurrentChatMessages } from "../../redux/whatsapp/whatsapp.actions";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { selectCurrentUser } from "../../redux/user/user.actions";
import useChat from "../../services/socket";
import http from "../../services/http";
import { ThemeContext } from "../ThemeContext/ThemeContext";
import { Done, DoneAll } from "@mui/icons-material";
import { rotuloDoAnexo } from "../../utils/anexoDaMensagem";

function resolveWhatsAppRoomId(chatItem) {
  if (!chatItem) return null;
  if (chatItem.room_id != null && chatItem.room_id !== '') return String(chatItem.room_id);
  const id = chatItem.id;
  if (typeof id === 'string' && id !== '') return id;
  if (id && typeof id === 'object') {
    if (id._serialized) return id._serialized;
    if (id.user != null) return String(id.user);
  }
  return null;
}

const ChatList = forwardRef(({ chats, handleSelectRoom, setMessages, selectedRoom, chat, ...props }, ref) => {
  const { theme } = useContext(ThemeContext);
  const [selectedChat, setSelectedChat] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const { eventEmit } = useWebSocket();
  const currentMessages = useSelector(selectCurrentMessages);
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const [unreadCount, setUnreadCount] = useState(0);

  const roomId = resolveWhatsAppRoomId(chat);

  const {
    messages,
    readMessage,
    sendFile
  } = useChat(roomId, chat);
  const mensagem = useChat(selectedRoom);

  const base64ToFile = (base64, filename, mimeType) => {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new File([ab], filename, { type: mimeType });
  };

  const messageReadFocus = () => {
    let foundReadMessage = false;
    currentMessages.slice().reverse().forEach(message => {
      if (foundReadMessage) return;

      if (!message.read_at && message.type === 0) {
        readMessage(message.id, true, message);
      } else if (message.read_at) {
        foundReadMessage = true;
      }
    });
  }

  useImperativeHandle(ref, () => ({
    sendMessage: (message, user, id, type, whatsapp) => {
      mensagem.sendMessage(message, user, id, type, whatsapp);
    },
    sendingMessageEvent: (event) => {
      mensagem.sendingMessageEvent(event);
    },
    readMessage: id => {
      readMessage(id);
    },
    sendFile: file => {
      sendFile(file);
    },
    setUnreadCount: count => {
      setUnreadCount(count);
    },
    getUnreadCount: () => {
      return unreadCount;
    },
    messageReadFocus: () => {
      messageReadFocus();
    }
  }));

  useEffect(() => {
    if (currentMessages.length > 0 && typeof setMessages === 'function') {
      const sortedMessages = currentMessages
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      setMessages(sortedMessages);
    }
  }, [currentMessages, setMessages]);

  const handleOpenChat = async (chatItem) => {
    setSelectedChat(chatItem);
    const id = resolveWhatsAppRoomId(chatItem);
    if (!id) return;
    const response = await http.get(`/messages/${id}`);
    const savedMessages = response.data;
    // dispatch(setCurrentChatMessages(savedMessages));
    eventEmit('whatsapp_open_chat', chatItem);
    setOpenModal(true);
  };

  const getLastMessage = (chat) => {
    const lastWhatsAppMessage = chat.lastMessage;
    const lastLocalMessage = currentMessages.length > 0
      ? currentMessages[currentMessages.length - 1]
      : null;

    const getText = (msg) => {
      const content = msg?.message || msg?.body || "";
      const shortText = content.length >= 15 ? content.substring(0, 15) + "..." : content;

      const renderAck = () => {
        if (msg.type !== 1) return null;
        if (msg?.ack === 3 || msg.meta?.ack === 3) {
          console.log(msg)
          return <DoneAll style={{ fontSize: '14px', color: '#63ACE5', marginLeft: '4px' }} />;
        } else if (msg?.ack === 2 || msg.meta?.ack === 2) {
          return <DoneAll style={{ fontSize: '14px', marginLeft: '4px' }} />;
        } else {
          return <Done style={{ fontSize: '14px', marginLeft: '4px' }} />;
        }
      };

      return (
        <span>
          {renderAck()}
          {shortText}
        </span>
      );
    };


    /*
     * A IMAGEM DO CHAT NUNCA CAIU AQUI (Gisele, 23/10/2026).
     *
     * `type` na tabela `messages` é BOOLEANO -- 0 cliente, 1 operador --,
     * então `type === "image"` nunca foi verdade para mensagem do site. A
     * prévia caía no texto, e o texto de um anexo é o nome gerado do arquivo:
     * o "código" que ela relatou.
     *
     * Quem sabe o que é anexo é `utils/anexoDaMensagem`, o mesmo que a lista
     * lateral e a fila do painel já usam. Era a terceira cópia da mesma
     * pergunta nesta tela, e as outras duas já estavam certas.
     *
     * O rótulo vem SEM emoji: a tela não usa emoji, e Gisele pediu
     * literalmente "apresentar apenas 'Imagem'".
     */
    if (lastLocalMessage && lastLocalMessage.room_id === roomId) {
      const rotulo = rotuloDoAnexo(lastLocalMessage.archive);

      return rotulo || getText(lastLocalMessage);
    }

    if (!lastWhatsAppMessage) {
      return "Sem mensagens";
    }

    if (lastWhatsAppMessage.type === 'vcard') {
      lastWhatsAppMessage.body = 'Cartão de Contato';
      getText(lastWhatsAppMessage);
    }

    if (lastWhatsAppMessage.type === 'sticker') {
      lastWhatsAppMessage.body = 'Sticker';
      getText(lastWhatsAppMessage);
    }
    if (lastWhatsAppMessage.type === 'notification_template' && lastWhatsAppMessage._data.subtype === 'contact_info_card') {
      lastWhatsAppMessage.body = 'Cartão de Contato';
      getText(lastWhatsAppMessage);
    }
    if (lastWhatsAppMessage.type === 'revoked') {
      lastWhatsAppMessage.body = 'Mensagem deletada para todos';
      getText(lastWhatsAppMessage);
    }
    if (lastWhatsAppMessage.type === 'location') {
      lastWhatsAppMessage.body = 'Localização enviada';
      getText(lastWhatsAppMessage);
    }
    if (lastWhatsAppMessage.type === 'document') {
      lastWhatsAppMessage.body = 'Documento enviado';
      getText(lastWhatsAppMessage);
    }
    if (lastWhatsAppMessage.type === 'video') {
      lastWhatsAppMessage.body = 'Vídeo enviado';
      getText(lastWhatsAppMessage);
    }
    if (lastWhatsAppMessage.type === 'audio') {
      lastWhatsAppMessage.body = 'Áudio enviado';
      getText(lastWhatsAppMessage);
    }

    // No WhatsApp o tipo É uma string, então este caminho sempre funcionou. O
    // texto acompanha o do chat para a lista não ter dois nomes para a mesma
    // coisa -- o pedido foi "apresentar apenas 'Imagem'".
    return lastWhatsAppMessage
      ? (lastWhatsAppMessage.type === "image"
        ? "Imagem"
        : getText(lastWhatsAppMessage))
      : "Sem mensagens";
  };

  if (!chat || !roomId) {
    return null;
  }

  return (
    <Box sx={{
      width: "100%", maxWidth: 400, bgcolor: "background.paper", backgroundColor: selectedRoom === roomId ? theme.palette.background.selected : theme.palette.secondary.main,
      '&:hover': {
        backgroundColor: theme.palette.background.hover,
      },
      borderRadius: 2,
      padding: "0px 4px",
    }}>
      <List>
        <ListItem button key={roomId}
          sx={{ backgroundColor: 'transparent', width: '100%' }}
          onClick={() => {
            handleOpenChat(chat);
            handleSelectRoom?.({ name: chat.name }, { ...chat, room_id: roomId, whatsapp: true });
          }}>
          <ListItemText
            sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            primary={(chat.name?.length >= 15 ? chat.name.substring(0, 15) + "..." : chat.name) || ''}
            secondary={getLastMessage(chat)}
          />
          <Grid sx={{ bgcolor: "transparent" }} container justifyContent={"flex-end"} alignItems={"center"}>
            {chat.lastMessage ?
              (chat.lastMessage.timestamp ?
                new Date(chat.lastMessage.timestamp * 1000).toLocaleTimeString('pt-BR', {
                  timeZone: 'America/Sao_Paulo',
                  hour: '2-digit',
                  minute: '2-digit'
                })
                : "Sem data")
              : "Sem mensagens"}
            <WhatsAppIcon sx={{ ml: 2 }} />
          </Grid>
        </ListItem>
      </List>
    </Box>
  );
});

export default ChatList;